import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../api/axios';

const formatDate = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
};

const formatCurrency = (value) => {
    const numeric = Number(value || 0);
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(numeric);
};

const STATUS_LABELS = {
    ENTWURF: 'Entwurf',
    GESENDET: 'Gesendet',
    ANGENOMMEN: 'Angenommen',
    ABGELEHNT: 'Abgelehnt',
    ABGELAUFEN: 'Abgelaufen',
};

const CONFIRM_ACTION = {
    ACCEPT: 'ACCEPT',
    DECLINE: 'DECLINE',
};

const OfferDetails = () => {
    const { id } = useParams();
    const queryClient = useQueryClient();
    const [confirmAction, setConfirmAction] = useState(null);

    const { data, isLoading, isError } = useQuery({
        queryKey: ['customer-offer', id],
        queryFn: () => axios.get(`/offers/${id}`).then((res) => res.data),
    });

    const acceptMutation = useMutation({
        mutationFn: () => axios.put(`/offers/${id}/accept`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['customer-offer', id] });
        },
    });

    const declineMutation = useMutation({
        mutationFn: () => axios.put(`/offers/${id}/decline`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['customer-offer', id] });
        },
    });

    const handleAccept = () => {
        setConfirmAction(CONFIRM_ACTION.ACCEPT);
    };

    const handleDecline = () => {
        setConfirmAction(CONFIRM_ACTION.DECLINE);
    };

    const closeConfirmModal = () => {
        if (acceptMutation.isPending || declineMutation.isPending) {
            return;
        }
        setConfirmAction(null);
    };

    const confirmCurrentAction = () => {
        if (confirmAction === CONFIRM_ACTION.ACCEPT) {
            acceptMutation.mutate(undefined, {
                onSettled: () => setConfirmAction(null),
            });
            return;
        }

        if (confirmAction === CONFIRM_ACTION.DECLINE) {
            declineMutation.mutate(undefined, {
                onSettled: () => setConfirmAction(null),
            });
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-600">
                Angebot wird geladen ...
            </div>
        );
    }

    if (isError || !data) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 text-red-600">
                Angebot konnte nicht gefunden werden.
            </div>
        );
    }

    const isFinal = data.status === 'ANGENOMMEN' || data.status === 'ABGELEHNT';
    const isConfirmOpen = confirmAction !== null;
    const isSubmittingDecision = acceptMutation.isPending || declineMutation.isPending;
    const isAcceptAction = confirmAction === CONFIRM_ACTION.ACCEPT;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 text-slate-900">
            <div className="max-w-5xl mx-auto px-4 py-12 space-y-8">
                <header className="bg-white/90 backdrop-blur rounded-3xl shadow-sm border border-slate-100 p-6 space-y-4">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="space-y-1">
                            <p className="text-sm text-slate-500">Angebot</p>
                            <h1 className="text-2xl font-semibold text-slate-900">{data.angebotNummer || '—'}</h1>
                            <p className="text-sm text-slate-500">Für {data.kundeName || 'Kunde'}</p>
                        </div>
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-sm font-semibold">
                            {STATUS_LABELS[data.status] || data.status}
                        </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4">
                            <p className="text-xs uppercase tracking-wide text-slate-500">Datum</p>
                            <p className="text-sm font-semibold text-slate-900">{formatDate(data.datum)}</p>
                        </div>
                        <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4">
                            <p className="text-xs uppercase tracking-wide text-slate-500">Gültig bis</p>
                            <p className="text-sm font-semibold text-slate-900">{formatDate(data.gueltigBis)}</p>
                        </div>
                        <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4">
                            <p className="text-xs uppercase tracking-wide text-slate-500">Projekt</p>
                            <p className="text-sm font-semibold text-slate-900">{data.projektName || '—'}</p>
                        </div>
                    </div>
                </header>

                <section className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 md:p-8 space-y-5">
                    <h2 className="text-lg font-semibold text-slate-900">Positionen</h2>

                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-slate-200">
                                    <th className="text-left py-2 pr-3 text-xs uppercase tracking-wide text-slate-500">Pos</th>
                                    <th className="text-left py-2 pr-3 text-xs uppercase tracking-wide text-slate-500">Beschreibung</th>
                                    <th className="text-right py-2 pr-3 text-xs uppercase tracking-wide text-slate-500">Menge</th>
                                    <th className="text-left py-2 pr-3 text-xs uppercase tracking-wide text-slate-500">Einheit</th>
                                    <th className="text-right py-2 pr-3 text-xs uppercase tracking-wide text-slate-500">Einzelpreis</th>
                                    <th className="text-right py-2 text-xs uppercase tracking-wide text-slate-500">Gesamt</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(data.positionen || []).map((position, idx) => (
                                    <tr key={idx} className="border-b border-slate-100 last:border-0">
                                        <td className="py-2 pr-3 text-slate-600">{position.pos || idx + 1}</td>
                                        <td className="py-2 pr-3 text-slate-900">{position.beschreibung || '—'}</td>
                                        <td className="py-2 pr-3 text-right text-slate-700">{position.menge ?? 0}</td>
                                        <td className="py-2 pr-3 text-slate-700">{position.einheit || '—'}</td>
                                        <td className="py-2 pr-3 text-right text-slate-700">{formatCurrency(position.einzelpreis)}</td>
                                        <td className="py-2 text-right font-medium text-slate-900">{formatCurrency(position.gesamt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="ml-auto max-w-sm space-y-2 pt-2">
                        <div className="flex justify-between text-sm text-slate-600">
                            <span>Netto</span>
                            <span>{formatCurrency(data.netto)}</span>
                        </div>
                        <div className="flex justify-between text-sm text-slate-600">
                            <span>MwSt {data.mwstSatz ?? 0} %</span>
                            <span>{formatCurrency(data.mwst)}</span>
                        </div>
                        <div className="flex justify-between text-base font-bold text-slate-900 pt-2 border-t border-slate-200">
                            <span>Gesamt</span>
                            <span>{formatCurrency(data.gesamt)}</span>
                        </div>
                    </div>

                    {data.notizen && (
                        <div className="rounded-xl bg-slate-50 border border-slate-100 p-4">
                            <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Notiz</p>
                            <p className="text-sm text-slate-700 whitespace-pre-wrap">{data.notizen}</p>
                        </div>
                    )}
                </section>

                <section className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-slate-900 mb-3">Angebot bestätigen</h2>
                    <p className="text-sm text-slate-600 mb-4">
                        Bitte bestätigen Sie, ob Sie dieses Angebot annehmen oder ablehnen möchten.
                    </p>
                    <div className="flex flex-wrap gap-3">
                        <button
                            type="button"
                            onClick={handleAccept}
                            disabled={isFinal || acceptMutation.isPending || declineMutation.isPending}
                            className="px-4 py-2 rounded-xl bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {acceptMutation.isPending ? 'Wird übernommen ...' : 'Annehmen'}
                        </button>
                        <button
                            type="button"
                            onClick={handleDecline}
                            disabled={isFinal || acceptMutation.isPending || declineMutation.isPending}
                            className="px-4 py-2 rounded-xl bg-red-600 text-white font-semibold text-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {declineMutation.isPending ? 'Wird gespeichert ...' : 'Ablehnen'}
                        </button>
                    </div>
                    {isFinal && (
                        <p className="text-xs text-slate-500 mt-3">
                            Dieses Angebot wurde bereits {data.status === 'ANGENOMMEN' ? 'angenommen' : 'abgelehnt'}.
                        </p>
                    )}
                </section>
            </div>

            {isConfirmOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[1px]"
                        onClick={closeConfirmModal}
                    />
                    <div className="relative w-full max-w-md rounded-2xl bg-white border border-slate-200 shadow-2xl p-6">
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">
                            {isAcceptAction ? 'Angebot annehmen?' : 'Angebot ablehnen?'}
                        </h3>
                        <p className="text-sm text-slate-600 mb-6">
                            {isAcceptAction
                                ? 'Sind Sie sicher, dass Sie dieses Angebot verbindlich annehmen möchten?'
                                : 'Sind Sie sicher, dass Sie dieses Angebot ablehnen möchten?'}
                        </p>

                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={closeConfirmModal}
                                disabled={isSubmittingDecision}
                                className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Abbrechen
                            </button>
                            <button
                                type="button"
                                onClick={confirmCurrentAction}
                                disabled={isSubmittingDecision}
                                className={`px-4 py-2 rounded-xl text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed ${
                                    isAcceptAction ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'
                                }`}
                            >
                                {isSubmittingDecision
                                    ? 'Wird gespeichert ...'
                                    : isAcceptAction
                                        ? 'Ja, annehmen'
                                        : 'Ja, ablehnen'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OfferDetails;
